import React, { useState } from 'react';
import { useTranslation } from '../../../shared/i18n';
import {
  useMonitoredActivities,
  useCreateMonitoredActivity,
  useUpdateMonitoredActivity,
  useDeleteMonitoredActivity,
} from '../hooks/useMonitoredActivities';

const ICON_TYPES = ['soccer', 'basketball', 'parkour', 'custom'];

const IconPreview = ({ iconType, customIconPath }) => {
  if (iconType === 'custom' && customIconPath) {
    return <img src={customIconPath} alt={iconType} style={{ width: 32, height: 32, borderRadius: 4 }} />;
  }
  const symbol = iconType === 'soccer' ? '⚽' : iconType === 'basketball' ? '🏀' : iconType === 'parkour' ? '🤸' : '📷';
  return <span style={{ fontSize: 28 }} aria-label={iconType}>{symbol}</span>;
};

const readFileAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const initialForm = { name: '', icon_type: 'soccer', length_minutes: 30, custom_icon: '' };

const MonitoredActivityForm = ({ activity, onSubmit, onCancel, submitting, t }) => {
  const [form, setForm] = useState(() =>
    activity
      ? {
          name: activity.name,
          icon_type: activity.icon_type,
          length_minutes: activity.length_minutes,
          custom_icon: '',
        }
      : initialForm
  );

  const handleIconFile = async (file) => {
    if (!file) return;
    const dataUrl = await readFileAsDataURL(file);
    setForm((f) => ({ ...f, custom_icon: dataUrl }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      name: form.name,
      icon_type: form.icon_type,
      length_minutes: Number(form.length_minutes),
    };
    if (form.icon_type === 'custom' && form.custom_icon) {
      payload.custom_icon = form.custom_icon;
    }
    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} data-testid="monitored-activity-form">
      <div className="mb-3">
        <label className="form-label">{t('guardian.monitored_activities.name_label')}</label>
        <input
          type="text"
          className="form-control"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          required
          data-testid="monitored-activity-name"
        />
      </div>

      <div className="mb-3">
        <label className="form-label">{t('guardian.monitored_activities.icon_label')}</label>
        <div className="d-flex gap-2 flex-wrap">
          {ICON_TYPES.map((it) => (
            <button
              type="button"
              key={it}
              className={`btn btn-sm ${form.icon_type === it ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setForm({ ...form, icon_type: it })}
              data-testid={`icon-choice-${it}`}
            >
              <IconPreview iconType={it} />
              <span className="ms-2">{t(`guardian.monitored_activities.icon.${it}`)}</span>
            </button>
          ))}
        </div>
      </div>

      {form.icon_type === 'custom' && (
        <div className="mb-3">
          <label className="form-label">{t('guardian.monitored_activities.custom_icon_label')}</label>
          <input
            type="file"
            className="form-control"
            accept="image/*"
            onChange={(e) => handleIconFile(e.target.files?.[0])}
            data-testid="monitored-activity-custom-icon"
          />
          {form.custom_icon && (
            <div className="mt-2">
              <img src={form.custom_icon} alt="preview" style={{ width: 48, height: 48, borderRadius: 4 }} />
            </div>
          )}
        </div>
      )}

      <div className="mb-3">
        <label className="form-label">{t('guardian.monitored_activities.length_label')}</label>
        <input
          type="number"
          min="1"
          max="240"
          className="form-control"
          value={form.length_minutes}
          onChange={(e) => setForm({ ...form, length_minutes: e.target.value })}
          required
          data-testid="monitored-activity-length"
        />
      </div>

      <div className="d-flex gap-2">
        <button type="submit" className="btn btn-primary" disabled={submitting} data-testid="monitored-activity-submit">
          {t('guardian.monitored_activities.save')}
        </button>
        <button type="button" className="btn btn-outline-secondary" onClick={onCancel}>
          {t('guardian.monitored_activities.cancel')}
        </button>
      </div>
    </form>
  );
};

export const MonitoredActivitiesPage = () => {
  const { t } = useTranslation();
  const { data: activities = [], isLoading, error } = useMonitoredActivities();
  const createMut = useCreateMonitoredActivity();
  const updateMut = useUpdateMonitoredActivity();
  const deleteMut = useDeleteMonitoredActivity();

  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const editing = activities.find((a) => a.id === editingId);

  const handleCreate = async (data) => {
    await createMut.mutateAsync(data);
    setShowForm(false);
  };

  const handleUpdate = async (data) => {
    await updateMut.mutateAsync({ id: editingId, data });
    setEditingId(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('guardian.monitored_activities.confirm_delete'))) return;
    await deleteMut.mutateAsync(id);
  };

  if (isLoading) return <div className="text-center py-4"><div className="spinner-border" role="status" /></div>;
  if (error) return <div className="alert alert-danger">{error.message}</div>;

  return (
    <div className="container py-4" data-testid="monitored-activities-page">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2 className="mb-0">{t('guardian.monitored_activities.title')}</h2>
        {!showForm && !editingId && (
          <button type="button" className="btn btn-primary" onClick={() => setShowForm(true)} data-testid="monitored-activity-new">
            {t('guardian.monitored_activities.new')}
          </button>
        )}
      </div>

      {showForm && (
        <div className="card mb-3"><div className="card-body">
          <MonitoredActivityForm t={t} submitting={createMut.isPending} onSubmit={handleCreate} onCancel={() => setShowForm(false)} />
        </div></div>
      )}

      {editing && (
        <div className="card mb-3"><div className="card-body">
          <MonitoredActivityForm t={t} activity={editing} submitting={updateMut.isPending} onSubmit={handleUpdate} onCancel={() => setEditingId(null)} />
        </div></div>
      )}

      {activities.length === 0 && !showForm && (
        <div className="alert alert-info">{t('guardian.monitored_activities.empty')}</div>
      )}

      <ul className="list-group">
        {activities.map((a) => (
          <li key={a.id} className="list-group-item d-flex align-items-center justify-content-between" data-testid={`monitored-activity-${a.id}`}>
            <div className="d-flex align-items-center gap-3">
              <IconPreview iconType={a.icon_type} customIconPath={a.custom_icon_path} />
              <div>
                <div className="fw-bold">{a.name}</div>
                <div className="text-muted small">
                  {t('guardian.monitored_activities.length_display', { minutes: a.length_minutes })}
                </div>
              </div>
            </div>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setEditingId(a.id)}>
                {t('guardian.monitored_activities.edit')}
              </button>
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(a.id)} data-testid={`monitored-activity-delete-${a.id}`}>
                {t('guardian.monitored_activities.delete')}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default MonitoredActivitiesPage;
